import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth, isViewingAs } from '../context/AuthContext';
import {
  enableAnalytics,
  disableAnalytics,
  resetAnalytics,
} from '../lib/analytics';

/**
 * AnalyticsGate — the ONLY place PostHog is switched on or off.
 *
 * Renders nothing. Watches the logged-in user + their consent state (same
 * ['me-consents', user.id] query the onboarding sheet and Privacy & data
 * page use, so grants/revokes made there propagate here instantly via the
 * shared react-query cache) and:
 *
 *  - enables PostHog (capture + session replay + identify-by-internal-id)
 *    ONLY when the user has 'analytics' granted AND answered under the
 *    CURRENT policy version (answeredScopes is version-aware server-side —
 *    lib/consents.js REASK_ON_POLICY_CHANGE — so consent given under the
 *    old "aggregate only" wording never enables replay);
 *  - disables it immediately on revoke;
 *  - never runs during platform-admin impersonation (the replay would be
 *    attributed to the impersonated member);
 *  - resets identity on logout so accounts on a shared device never link.
 */
export default function AnalyticsGate() {
  const { user, loading, impersonating } = useAuth();
  // B-71: the robust check, not just the in-memory `impersonating` (gone
  // after a reload mid view-as, which let PostHog switch on as the member).
  const viewingAs = isViewingAs(impersonating);

  const enabled = !!user && !loading && !viewingAs && !user.isPlatformUser;
  const { data } = useQuery({
    queryKey: ['me-consents', user?.id],
    queryFn: () => axios.get('/me/consents').then((r) => r.data),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const granted =
    !!data?.consents?.analytics &&
    (data?.answeredScopes || []).includes('analytics');

  useEffect(() => {
    if (!user) {
      resetAnalytics(); // logout — drop identity, stop capture
      return;
    }
    if (viewingAs || user.isPlatformUser) {
      disableAnalytics();
      return;
    }
    if (!data) return; // consent state not loaded yet — stay off
    if (granted) enableAnalytics(user.id);
    else disableAnalytics();
  }, [user, viewingAs, data, granted]);

  return null;
}
